"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { parse, isValid, startOfDay } from "date-fns";
import {
  maskDate,
  maskDecimal,
  parseStringToNumber,
} from "../utils/formatters";
import { calcularMarcacaoAMercado } from "../utils/math/core";
import { simularNovoInvestimento } from "../utils/math/scenarios";
import { CalculoCompleto, CenarioSimulado } from "../types/calculator";
import { comporTaxaNominal } from "../utils/math/rates";
import {
  MainCard,
  MainCardTitle,
  MainCardContent,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";

const formSchema = z
  .object({
    tipoTitulo: z.enum(["PREFIXADO", "IPCA"]),
    taxaInflacao: z.string().optional(),
    dataAplicacao: z
      .string()
      .min(10, "Data incompleta")
      .refine((val) => isValid(parse(val, "dd/MM/yyyy", new Date())), {
        message: "Data inválida",
      }),
    dataVencimento: z
      .string()
      .min(10, "Data incompleta")
      .refine((val) => isValid(parse(val, "dd/MM/yyyy", new Date())), {
        message: "Data inválida",
      }),
    valorInvestido: z
      .string()
      .min(1, "Obrigatório")
      .refine((v) => parseStringToNumber(v) > 0, "Maior que zero"),
    valorAtualBruto: z
      .string()
      .min(1, "Obrigatório")
      .refine((v) => parseStringToNumber(v) > 0, "Maior que zero"),
    rentabilidadeContratada: z
      .string()
      .min(1, "Obrigatório")
      .refine((v) => parseStringToNumber(v) > 0, "Maior que zero"),
    isentoIR: z.boolean(),
    taxaOfertada: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const aplicacao = parse(data.dataAplicacao, "dd/MM/yyyy", new Date());
    const vencimento = parse(data.dataVencimento, "dd/MM/yyyy", new Date());
    const hoje = startOfDay(new Date());

    if (isValid(aplicacao) && isValid(vencimento)) {
      if (vencimento <= aplicacao) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Deve ser após a aplicação",
          path: ["dataVencimento"],
        });
      }
      if (vencimento <= hoje) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Título já venceu",
          path: ["dataVencimento"],
        });
      }
      if (aplicacao > hoje) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Não pode ser no futuro",
          path: ["dataAplicacao"],
        });
      }
    }
  });

export type CalculatorFormData = z.infer<typeof formSchema>;

export type ResultadoMarcacao = {
  core: CalculoCompleto;
  simulacaoIsenta?: CenarioSimulado;
  simulacaoTributada?: CenarioSimulado;
  montanteFicar: number;
  isIPCA: boolean;
  ipcaUtilizado: number;
};

export const MarcacaoMercadoInputCard = ({
  setResultado,
}: {
  setResultado: (value: ResultadoMarcacao | null) => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CalculatorFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      tipoTitulo: "PREFIXADO",
      taxaInflacao: "4,50",
      isentoIR: false,
      taxaOfertada: "",
    },
  });

  const tipoTituloWatch = watch("tipoTitulo");
  const isentoIRWatch = watch("isentoIR");

  const handleDateChange =
    (field: "dataAplicacao" | "dataVencimento") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, maskDate(e.target.value), { shouldValidate: true });
    };

  const handleDecimalChange =
    (
      field:
        | "valorInvestido"
        | "valorAtualBruto"
        | "rentabilidadeContratada"
        | "taxaOfertada"
        | "taxaInflacao",
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, maskDecimal(e.target.value), { shouldValidate: true });
    };

  const onSubmit = (data: CalculatorFormData) => {
    const isIPCA = data.tipoTitulo === "IPCA";
    const ipcaUtilizado = isIPCA
      ? parseStringToNumber(data.taxaInflacao || "4,50")
      : 0;

    const taxaDigitadaContratada = parseStringToNumber(
      data.rentabilidadeContratada,
    );
    const taxaNominalParaMotor = comporTaxaNominal(
      taxaDigitadaContratada,
      ipcaUtilizado,
    );

    const inputParaMotor = {
      dataAplicacao: parse(data.dataAplicacao, "dd/MM/yyyy", new Date()),
      dataVencimento: parse(data.dataVencimento, "dd/MM/yyyy", new Date()),
      dataAnalise: startOfDay(new Date()),
      valorInvestido: parseStringToNumber(data.valorInvestido),
      valorAtualBruto: parseStringToNumber(data.valorAtualBruto),
      rentabilidadeContratada: taxaNominalParaMotor,
      isentoIR: data.isentoIR,
    };

    const calcCompleto = calcularMarcacaoAMercado(inputParaMotor);

    let simIsenta, simTributada;
    if (data.taxaOfertada && parseStringToNumber(data.taxaOfertada) > 0) {
      const taxaDigitadaOferta = parseStringToNumber(data.taxaOfertada);
      const taxaNominalOferta = comporTaxaNominal(
        taxaDigitadaOferta,
        ipcaUtilizado,
      );

      const valLiqAtual = calcCompleto.intermediarios.valorAtualLiquido;
      const anosFaltantes = calcCompleto.intermediarios.anosFaltantes;
      const diasFaltantes = calcCompleto.intermediarios.diasFaltantes;
      const valLiqOrig = calcCompleto.intermediarios.valorFinalLiquidoOriginal;

      simIsenta = simularNovoInvestimento(
        taxaNominalOferta,
        true,
        valLiqAtual,
        anosFaltantes,
        diasFaltantes,
        valLiqOrig,
      );
      simTributada = simularNovoInvestimento(
        taxaNominalOferta,
        false,
        valLiqAtual,
        anosFaltantes,
        diasFaltantes,
        valLiqOrig,
      );
    }

    setResultado({
      core: calcCompleto,
      simulacaoIsenta: simIsenta,
      simulacaoTributada: simTributada,
      montanteFicar: calcCompleto.intermediarios.valorFinalLiquidoOriginal,
      isIPCA,
      ipcaUtilizado,
    });
  };

  return (
    <MainCard>
      <MainCardTitle>Calculadora de Marcação a Mercado</MainCardTitle>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register("tipoTitulo")} />

        {/* Seletor de tipo de título */}
        <div className="flex items-center bg-gray-100 p-1 rounded-md border border-gray-200 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setValue("tipoTitulo", "PREFIXADO")}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200 ${
              tipoTituloWatch === "PREFIXADO"
                ? "bg-white text-calculator-text shadow-sm"
                : "text-gray-500 hover:text-calculator-text"
            }`}
          >
            Prefixado
          </button>
          <button
            type="button"
            onClick={() => setValue("tipoTitulo", "IPCA")}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-all duration-200 ${
              tipoTituloWatch === "IPCA"
                ? "bg-white text-calculator-text shadow-sm"
                : "text-gray-500 hover:text-calculator-text"
            }`}
          >
            IPCA +
          </button>
        </div>

        {/* Datas */}
        <MainCardContent>
          <div className="space-y-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="data-aplicacao-id"
            >
              Data de Aplicação
            </label>
            <input
              id="data-aplicacao-id"
              {...register("dataAplicacao")}
              onChange={handleDateChange("dataAplicacao")}
              placeholder="dd/mm/aaaa"
              inputMode="numeric"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
            />
            {errors.dataAplicacao && (
              <span className="text-xs text-red-600 font-semibold">
                {errors.dataAplicacao.message}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="data-vencimento-id"
            >
              Data de Vencimento
            </label>
            <input
              id="data-vencimento-id"
              {...register("dataVencimento")}
              onChange={handleDateChange("dataVencimento")}
              placeholder="dd/mm/aaaa"
              inputMode="numeric"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
            />
            {errors.dataVencimento && (
              <span className="text-xs text-red-600 font-semibold">
                {errors.dataVencimento.message}
              </span>
            )}
          </div>
        </MainCardContent>

        {/* Valores */}
        <MainCardContent>
          <div className="space-y-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="valor-investido-id"
            >
              Valor Investido (R$)
            </label>
            <input
              id="valor-investido-id"
              {...register("valorInvestido")}
              onChange={handleDecimalChange("valorInvestido")}
              placeholder="Ex: 10.000,00"
              inputMode="numeric"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
            />
            {errors.valorInvestido && (
              <span className="text-xs text-red-600 font-semibold">
                {errors.valorInvestido.message}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="valor-atual-id"
            >
              Valor Atual Bruto (R$)
            </label>
            <input
              id="valor-atual-id"
              {...register("valorAtualBruto")}
              onChange={handleDecimalChange("valorAtualBruto")}
              placeholder="Ex: 11.500,50"
              inputMode="numeric"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
            />
            {errors.valorAtualBruto && (
              <span className="text-xs text-red-600 font-semibold">
                {errors.valorAtualBruto.message}
              </span>
            )}
          </div>
        </MainCardContent>

        {/* Rentabilidade e isenção de IR */}
        <MainCardContent>
          <div className="space-y-2">
            <div className="flex justify-between items-center h-7">
              <label
                className="block font-medium text-sm"
                htmlFor="rentabilidade-id"
              >
                {tipoTituloWatch === "IPCA"
                  ? "Taxa Fixa do IPCA+ (%)"
                  : "Rentabilidade Contratada (% a.a)"}
              </label>
              <div
                className={`flex items-center space-x-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200 transition-opacity duration-300 ${
                  tipoTituloWatch === "IPCA"
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none select-none"
                }`}
              >
                <span>IPCA Base:</span>
                <input
                  {...register("taxaInflacao")}
                  onChange={handleDecimalChange("taxaInflacao")}
                  className="w-10 bg-transparent text-center font-semibold text-calculator-text focus:outline-none"
                  tabIndex={tipoTituloWatch === "IPCA" ? 0 : -1}
                />
                <span>%</span>
              </div>
            </div>
            <input
              id="rentabilidade-id"
              {...register("rentabilidadeContratada")}
              onChange={handleDecimalChange("rentabilidadeContratada")}
              placeholder="Ex: 12,50"
              inputMode="numeric"
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
            />
            {errors.rentabilidadeContratada && (
              <span className="text-xs text-red-600 font-semibold">
                {errors.rentabilidadeContratada.message}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center h-7">
              <label className="block font-medium text-sm">
                O título atual é isento de Imposto de Renda?
              </label>
            </div>
            <div className="flex items-center bg-gray-100 p-1 rounded-md border border-gray-200 h-10 w-full">
              <input
                type="checkbox"
                className="hidden"
                {...register("isentoIR")}
              />
              <button
                type="button"
                onClick={() =>
                  setValue("isentoIR", false, { shouldValidate: true })
                }
                className={`flex-1 py-1.5 h-full text-sm font-medium rounded transition-all duration-200 ${
                  !isentoIRWatch
                    ? "bg-white text-calculator-text shadow-sm"
                    : "text-gray-500 hover:text-calculator-text"
                }`}
              >
                Não
              </button>
              <button
                type="button"
                onClick={() =>
                  setValue("isentoIR", true, { shouldValidate: true })
                }
                className={`flex-1 py-1.5 h-full text-sm font-medium rounded transition-all duration-200 ${
                  isentoIRWatch
                    ? "bg-white text-calculator-text shadow-sm"
                    : "text-gray-500 hover:text-calculator-text"
                }`}
              >
                Sim
              </button>
            </div>
          </div>
        </MainCardContent>

        <hr className="border-gray-300" />

        {/* Taxa ofertada (opcional) */}
        <div className="bg-white p-4 rounded-md border border-gray-200">
          <div className="space-y-2">
            <label
              className="block mb-1 font-bold text-sm"
              htmlFor="taxa-ofertada-id"
            >
              Você já tem uma oportunidade em mente? (Opcional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Insira a taxa de um título que você encontrou para projetarmos a
              diferença na prática.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1 w-full">
                <input
                  id="taxa-ofertada-id"
                  {...register("taxaOfertada")}
                  onChange={handleDecimalChange("taxaOfertada")}
                  placeholder={
                    tipoTituloWatch === "IPCA"
                      ? "Nova taxa IPCA+ (%)"
                      : "Rentabilidade da nova oferta (% a.a)"
                  }
                  inputMode="numeric"
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-calculator-primary"
                />
              </div>
              <Button
                type="submit"
                variant="default"
                className="sm:w-[150px] w-full"
              >
                Calcular
              </Button>
            </div>
          </div>
        </div>
      </form>
    </MainCard>
  );
};
